/*
 * Copyright (c) 2022, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    graphqlWrapper: {
      textAlign: 'left',
      width: '100%',
      margin: `0px ${theme.spacing(2)}px`,
      '& > .graphiql-container': {
        border: `1px solid #ccd1f2`,
        borderRadius: theme.spacing(0.5),
        height: 'calc(100vh - 23rem)',
      },
      '& .graphiql-container .docExplorerResizer': {
        height: 'inherit',
      },
      '& .topBarWrap': {
        '& .topBar': {
          background: theme.palette.common.white,
          borderBottom: '1px solid #ccd1f2',
          height: 'unset',
          padding: theme.spacing(1),
          '& .title': {
            display: 'none',
          },
          '& .execute-button-wrap': {
            margin: `0 ${theme.spacing(1)}px`,
            '& .execute-button': {
              background: '#5567d5',
              border: `1px solid ${theme.palette.secondary.light}`,
              boxShadow: `0 1px 0 ${theme.palette.common.white}`,
              fill: theme.palette.secondary.light,
              transitionDuration: '0.3s',
              '&:hover': {
                border: '1px solid #4d5ec0',
                fill: theme.palette.common.white,
              },
            },
          },
        },
        '& .toolbar': {
          alignItems: 'center',
          '& button': {
            color: '#222228',
            fontSize: theme.spacing(1.5),
            height: theme.spacing(4),
            margin: `0 ${theme.spacing(0.5)}px`,
          },
        },
        '& .docExplorerShow': {
          background: theme.palette.common.white,
          borderBottom: `1px solid #ccd1f2`,
          borderLeft: 'none',
          color: '#5567d5',
          '&:before': {
            borderLeft: '2px solid #5567d5',
            borderTop: '2px solid #5567d5',
            marginBottom: 0,
          },
        },
      },
      '& .CodeMirror-gutters': {
        background: '#f7f8f',
        borderRight: 'none',
      },
      '& .editorBar': {
        '& .secondary-editor-title, & .editor-drag-bar': {
          background: '#f7f8f',
        },
        '& .secondary-editor-title': {
          borderBottom: '1px solid #ccd1f2',
          borderTop: '1px solid #ccd1f2',
        },
        '& .editor-drag-bar': {
          borderRight: '1px solid #ccd1f2',
          borderLeft: '1px solid #ccd1f2',
        },
        '& .resultWrap': {
          '& .CodeMirror-scroll': {
            background: theme.palette.common.white,
            '& .CodeMirror-gutter': {
              background: theme.palette.common.white,
            },
          },
        },
        '& .queryWrap, & .resultWrap': {
          '& .CodeMirror-widget': {
            '& .CodeMirror-foldmarker': {
              background: '#5567d5',
            },
          },
        },
      },
      '& .historyPaneWrap': {
        borderTopLeftRadius: theme.spacing(0.5),
        borderBottomLeftRadius: theme.spacing(0.5),
      },
      '& .history-contents': {
        borderBottomLeftRadius: theme.spacing(0.5),
        '& >li': {
          '&:hover, &:active': {
            background: '#ccd1f2',
          },
          '& >input': {
            border: `1px solid #ccd1f2`,
            borderRadius: theme.spacing(0.5),
            padding: theme.spacing(0.5),
            outline: 'none',
          },
        },
      },
      '& .docExplorerWrap': {
        zIndex: `${10}`,
        '& .variable-editor-title': {
          margin: `${theme.spacing(1)}px 0`,
          '& > select': {
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.spacing(0.5),
            margin: `0 ${theme.spacing(1)}px`,
            padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
            '&:focus': {
              outline: 'none',
            },
          },
        },
        '& .graphiql-explorer-root': {
          '& > div': {
            overflow: 'auto !important',
            paddingLeft: theme.spacing(0.5),
          },
        },
        '& .graphiql-operation-title-bar': {
          '& span': {
            '& > input': {
              width: 'auto',
            },
          },
        },
      },
      '& .docExplorerWrap, & .historyPaneWrap': {
        '& .doc-explorer-title-bar, & .history-title-bar': {
          height: 'unset',
          padding: theme.spacing(1),
          '& .doc-explorer-title, & .history-title': {
            color: theme.palette.grey[500],
            textAlign: 'left',
          },
        },
        '& .doc-explorer-contents, & .history-contents': {
          borderTop: '1px solid #ccd1f2',
          padding: theme.spacing(2),
          top: 50,
          '& .search-box': {
            margin: `0 0 ${theme.spacing(1)}px 0`,
            '& .search-box-icon': {
              fontSize: 20,
            },
            '& >input': {
              padding: `0 ${theme.spacing(1)}px`,
            },
          },
          '& .doc-type-description': {
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.spacing(1.5),
          },
        },
      },
    },
    endpointTextField: {
      border: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: theme.spacing(0.5),
      boxShadow: `inset 0 0 1px 0 ${theme.palette.grey[200]}`,
      height: theme.spacing(4),
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
      transition: 'border 0.3s',
      '&:hover, &:active': {
        border: `1px solid #ccd1f2`,
        overflow: 'auto',
      },
      '& .MuiInputBase-input': {
        fontFamily: theme.typography.fontFamily,
        padding: theme.spacing(0.25),
      },
      '& .MuiInputAdornment-positionStart': {
        marginRight: 0,
      },
    },
    endpointTextFieldError: {
      border: '1px solid #fe523c',
      background: '#FAE8E8',
      '&:hover, &:active': {
        border: '1px solid #fe523c',
        background: '#FAE8E8',
      },
    },
    greyOutlinedButton: {
      left: 0,
      fontFamily: 'GilmerMedium',
      fontSize: '12px',
      lineHeight: '14px',
      color: '#40404B',
      height: theme.spacing(5),
      width: theme.spacing(10),
    },
    greyOutlineBtnRoot: {
      textTransform: 'capitalize',
      borderRadius: '5px',
      backgroundColor: theme.palette.secondary.light,
      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.05)',
      '&:hover': {
        backgroundColor: theme.palette.secondary.light,
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06), 0 2px 4px 0 rgba(0,0,0,0.12)',
      },
      '&:focus, &:active': {
        backgroundColor: theme.palette.secondary.light,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.07) 0%, rgba(0,0,0,0) 100%)',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08), 0 0 0 0 rgba(0,0,0,0.07)',
      },
    },
    disabledBtn: {
      opacity: 0.5,
    }
  })
);
